import { useEffect, useMemo, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3, List, ListOrdered, Quote,
  Link as LinkIcon, Table as TableIcon, Undo2, Redo2, Eraser,
  AlignLeft,
} from "lucide-react";
import { marked } from "marked";
import TurndownService from "turndown";

/**
 * WysiwygEditor, a TipTap-powered visual editor that reads/writes Markdown.
 *
 * - Input/Output: Markdown (so the existing backend PDF pipeline is unchanged).
 * - Internally renders rich HTML for an executive-grade editing experience.
 * - Supports headings (h1-h3), bold, italic, underline, strikethrough,
 *   ordered & unordered lists, blockquotes, links, and tables.
 */

// Configure turndown for clean GFM-style output the backend already parses.
const turndown = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
  emDelimiter: "*",
});

// GFM-style tables (turndown drops them by default, re-enable manually).
turndown.addRule("gfmTable", {
  filter: "table",
  replacement: (_content, node) => {
    const rows = Array.from(node.querySelectorAll("tr"));
    if (!rows.length) return "";
    const cells = (tr) =>
      Array.from(tr.querySelectorAll("th,td")).map((c) =>
        (c.textContent || "").trim().replace(/\|/g, "\\|")
      );
    const header = cells(rows[0]);
    const lines = [
      `| ${header.join(" | ")} |`,
      `| ${header.map(() => "---").join(" | ")} |`,
      ...rows.slice(1).map((tr) => `| ${cells(tr).join(" | ")} |`),
    ];
    return `\n\n${lines.join("\n")}\n\n`;
  },
});

// Strip soft line breaks inside paragraphs so backend markdown stays clean.
turndown.addRule("brToSpace", {
  filter: "br",
  replacement: () => " ",
});

const markdownToHtml = (md) => {
  if (!md) return "";
  try {
    return marked.parse(md, { breaks: false, gfm: true });
  } catch {
    return md;
  }
};

const htmlToMarkdown = (html) => {
  if (!html) return "";
  try {
    return turndown.turndown(html).trim();
  } catch {
    return "";
  }
};

export default function WysiwygEditor({ value, onChange, placeholder }) {
  // Track the last markdown we *emitted* so we can ignore echo updates
  // (otherwise every keystroke would round-trip md->html->md and lose caret).
  const lastEmittedRef = useRef("");

  // Initial HTML for the editor ,  computed once on mount; subsequent value
  // changes are handled by the sync useEffect below (which preserves caret).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialHtml = useMemo(() => markdownToHtml(value || ""), []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: { HTMLAttributes: { class: "wy-code" } },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: "wy-link" },
      }),
      Table.configure({ resizable: false, HTMLAttributes: { class: "wy-table" } }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: initialHtml,
    editorProps: {
      attributes: {
        class:
          "wysiwyg-doc tiptap focus:outline-none min-h-[420px] px-5 py-4 bg-white text-[#101010] rounded-b-sm",
        "data-testid": "wysiwyg-editor",
        spellcheck: "true",
      },
    },
    onUpdate: ({ editor: ed }) => {
      const md = htmlToMarkdown(ed.getHTML());
      lastEmittedRef.current = md;
      onChange?.(md);
    },
  });

  // External value sync (template load, AI-seeded content). Only update when
  // the incoming value differs from what we last emitted (ignore echo).
  useEffect(() => {
    if (!editor) return;
    const incoming = value || "";
    if (incoming === lastEmittedRef.current) return;
    const nextHtml = markdownToHtml(incoming);
    // Avoid resetting caret if the rendered HTML is already identical.
    if (nextHtml === editor.getHTML()) return;
    editor.commands.setContent(nextHtml, false);
    lastEmittedRef.current = incoming;
  }, [value, editor]);

  if (!editor) {
    return (
      <div className="bg-white text-[#101010] rounded-sm border border-white/10 min-h-[480px] px-5 py-4 text-sm text-slate-500">
        Loading editor…
      </div>
    );
  }

  return (
    <div className="border border-white/10 rounded-sm overflow-hidden bg-white" data-testid="wysiwyg-shell">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} placeholder={placeholder} />
      <EditorStyles />
    </div>
  );
}

function ToolbarBtn({ onClick, active, disabled, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testid}
      className={`h-7 w-7 inline-flex items-center justify-center rounded-sm transition-colors ${
        active
          ? "bg-emerald-500/20 text-emerald-700"
          : "text-slate-700 hover:bg-slate-100"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function ToolbarSep() {
  return <span className="h-5 w-px bg-slate-200 mx-1" />;
}

function Toolbar({ editor }) {
  const insertLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  return (
    <div
      className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-[#F8FAFC] border-b border-slate-200"
      data-testid="wysiwyg-toolbar"
    >
      <ToolbarBtn
        icon={Bold}
        label="Bold"
        testid="wy-bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarBtn
        icon={Italic}
        label="Italic"
        testid="wy-italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarBtn
        icon={UnderlineIcon}
        label="Underline"
        testid="wy-underline"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      <ToolbarBtn
        icon={Strikethrough}
        label="Strikethrough"
        testid="wy-strike"
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />
      <ToolbarSep />
      <ToolbarBtn
        icon={Heading1}
        label="Heading 1"
        testid="wy-h1"
        active={editor.isActive("heading", { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      />
      <ToolbarBtn
        icon={Heading2}
        label="Heading 2"
        testid="wy-h2"
        active={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <ToolbarBtn
        icon={Heading3}
        label="Heading 3"
        testid="wy-h3"
        active={editor.isActive("heading", { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      />
      <ToolbarBtn
        icon={AlignLeft}
        label="Paragraph"
        testid="wy-p"
        active={editor.isActive("paragraph")}
        onClick={() => editor.chain().focus().setParagraph().run()}
      />
      <ToolbarSep />
      <ToolbarBtn
        icon={List}
        label="Bulleted list"
        testid="wy-ul"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarBtn
        icon={ListOrdered}
        label="Numbered list"
        testid="wy-ol"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolbarBtn
        icon={Quote}
        label="Quote (callout)"
        testid="wy-quote"
        active={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <ToolbarSep />
      <ToolbarBtn icon={LinkIcon} label="Insert/Edit link" testid="wy-link" active={editor.isActive("link")} onClick={insertLink} />
      <ToolbarBtn icon={TableIcon} label="Insert table" testid="wy-table" onClick={insertTable} />
      <ToolbarSep />
      <ToolbarBtn
        icon={Eraser}
        label="Clear formatting"
        testid="wy-clear"
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
      />
      <ToolbarSep />
      <ToolbarBtn
        icon={Undo2}
        label="Undo"
        testid="wy-undo"
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      />
      <ToolbarBtn
        icon={Redo2}
        label="Redo"
        testid="wy-redo"
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      />
    </div>
  );
}

function EditorStyles() {
  // Scoped styles for the visible content area. The editor lives on a WHITE
  // surface (mirrors final PDF) inside the otherwise dark drawer.
  return (
    <style>{`
      .wysiwyg-doc { font-family: Calibri, "Helvetica Neue", Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.55; color: #101010; }
      .wysiwyg-doc:focus { outline: none; }
      .wysiwyg-doc p { margin: 0 0 8px; }
      .wysiwyg-doc h1 { font-size: 22px; font-weight: 700; color: #0A2B6B; margin: 14px 0 6px; padding-bottom: 4px; border-bottom: 2px solid #10B981; text-transform: uppercase; letter-spacing: 0.5px; }
      .wysiwyg-doc h2 { font-size: 17px; font-weight: 700; color: #0A2B6B; margin: 12px 0 4px; padding-bottom: 2px; border-bottom: 1px solid #10B981; text-transform: uppercase; letter-spacing: 0.3px; }
      .wysiwyg-doc h3 { font-size: 14px; font-weight: 700; color: #101010; margin: 10px 0 3px; }
      .wysiwyg-doc strong { font-weight: 700; }
      .wysiwyg-doc em { font-style: italic; }
      .wysiwyg-doc u { text-decoration: underline; }
      .wysiwyg-doc s { text-decoration: line-through; color: #6B7280; }
      .wysiwyg-doc ul, .wysiwyg-doc ol { margin: 4px 0 8px; padding-left: 22px; }
      .wysiwyg-doc li { margin: 2px 0; }
      .wysiwyg-doc blockquote {
        border-left: 3px solid #10B981;
        background: #F8FAFC;
        padding: 8px 14px;
        margin: 10px 0;
        color: #1F2937;
        font-style: italic;
      }
      .wysiwyg-doc a.wy-link, .wysiwyg-doc a { color: #0A2B6B; text-decoration: underline; }
      .wysiwyg-doc table.wy-table, .wysiwyg-doc table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 13px; }
      .wysiwyg-doc th { background: #0A2B6B; color: white; padding: 6px 8px; text-align: left; font-weight: 700; border: 1px solid #0A2B6B; }
      .wysiwyg-doc td { padding: 6px 8px; border: 1px solid #E2E8F0; }
      .wysiwyg-doc tr:nth-child(even) td { background: #F8FAFC; }
      .wysiwyg-doc .wy-code, .wysiwyg-doc pre { background: #F1F5F9; border: 1px solid #E2E8F0; padding: 8px 10px; border-radius: 4px; font-family: Consolas, "Courier New", monospace; font-size: 12.5px; overflow-x: auto; }
      .wysiwyg-doc code { background: #F1F5F9; padding: 1px 4px; border-radius: 3px; font-family: Consolas, "Courier New", monospace; font-size: 12.5px; }
      .wysiwyg-doc hr { border: none; border-top: 1px solid #CBD5E1; margin: 12px 0; }
      .wysiwyg-doc.tiptap p.is-editor-empty:first-child::before {
        color: #94A3B8;
        content: attr(data-placeholder);
        float: left;
        height: 0;
        pointer-events: none;
      }
      /* Selected cell highlight inside a Tiptap table */
      .wysiwyg-doc .selectedCell { background: #DBEAFE; }
    `}</style>
  );
}
